import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import {
  Users, Crown, ShoppingBag, Heart, ArrowLeft, Loader2,
  Calendar, UserCheck, UserPlus, AlertCircle
} from 'lucide-react';
import { API } from './config';

const authHeaders = () => {
  const token = Cookies.get('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch (e) { return '—'; }
};

const PageShell = ({ children, testId }) => (
  <div className="min-h-screen bg-gray-50 p-4 md:p-8" data-testid={testId}>
    <div className="max-w-2xl mx-auto">
      <Button variant="ghost" onClick={() => window.history.back()} className="mb-4 text-gray-600" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
      {children}
    </div>
  </div>
);

const ErrorFallback = ({ message, onRetry }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="error-state">
    <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
    <h3 className="text-lg font-semibold text-gray-700 mb-1">Something went wrong</h3>
    <p className="text-sm text-gray-500 max-w-xs mb-4">{message || 'Please try again later.'}</p>
    <div className="flex gap-2">
      {onRetry && (
        <Button onClick={onRetry} className="bg-[#0f1d3d] hover:bg-[#1a2d55] text-white" data-testid="error-retry-btn">
          Retry
        </Button>
      )}
      <Button variant="outline" onClick={() => window.history.back()} data-testid="error-back-btn">Go Back</Button>
    </div>
  </div>
);

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
      <Icon className="h-8 w-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-700 mb-1">{title}</h3>
    <p className="text-sm text-gray-500 max-w-xs">{description}</p>
  </div>
);

const PersonRow = ({ name, picture, badge, badgeColor, detail, detailIcon: DetailIcon }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
    <div className="flex items-center gap-3">
      <Avatar className="h-10 w-10">
        {picture && <AvatarImage src={picture} />}
        <AvatarFallback className="bg-gradient-to-br from-blue-400 to-purple-500 text-white text-sm">
          {name?.charAt(0)?.toUpperCase() || 'U'}
        </AvatarFallback>
      </Avatar>
      <div>
        <p className="font-medium text-gray-800 text-sm">{name}</p>
        {detail && (
          <p className="text-xs text-gray-500 flex items-center gap-1">
            {DetailIcon && <DetailIcon className="h-3 w-3" />}
            {detail}
          </p>
        )}
      </div>
    </div>
    {badge && (
      <Badge className={badgeColor || 'bg-green-100 text-green-700'}>{badge}</Badge>
    )}
  </div>
);

// ─── My Subscribers (Creator) ───
export const MySubscribersPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/creator/subscribers`, { headers: authHeaders(), withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => {
        if (e.response?.status === 401 || e.response?.status === 403) {
          setError('Please log in as a creator to view subscribers.');
        } else {
          setData({ subscribers: [], total: 0 });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (error) return <PageShell testId="my-subscribers-page"><ErrorFallback message={error} /></PageShell>;

  return (
    <PageShell testId="my-subscribers-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Crown className="h-5 w-5 text-purple-500" /> My Subscribers
            {data && <Badge variant="outline" className="ml-auto">{data.total}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-purple-500" /></div>
          ) : !data?.subscribers?.length ? (
            <EmptyState icon={Crown} title="No subscribers yet" description="When users subscribe to your content, they'll appear here." />
          ) : (
            (data.subscribers || []).map((s, i) => (
              <PersonRow key={i}
                name={s.user_name || 'User'}
                picture={s.profile_picture}
                badge={s.status === 'active' ? 'Active' : (s.status || 'Unknown')}
                badgeColor={s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}
                detail={`Since ${formatDate(s.started_at)}${s.price > 0 ? ` · $${s.price}/mo` : ''}`}
                detailIcon={Calendar}
              />
            ))
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
};

// ─── My Subscriptions (User) ───
export const MySubscriptionsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/user/subscriptions`, { headers: authHeaders(), withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => {
        if (e.response?.status === 401) {
          setError('Please log in to view your subscriptions.');
        } else {
          setData({ subscriptions: [], total: 0 });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (error) return <PageShell testId="my-subscriptions-page"><ErrorFallback message={error} /></PageShell>;

  return (
    <PageShell testId="my-subscriptions-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Crown className="h-5 w-5 text-pink-500" /> My Subscriptions
            {data && <Badge variant="outline" className="ml-auto">{data.total}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-pink-500" /></div>
          ) : !data?.subscriptions?.length ? (
            <EmptyState icon={Crown} title="No subscriptions" description="Subscribe to creators to access their exclusive content." />
          ) : (
            (data.subscriptions || []).map((s, i) => (
              <PersonRow key={i}
                name={s.creator_name || 'Creator'}
                picture={s.profile_picture}
                badge={s.status === 'active' ? 'Active' : (s.status || 'Unknown')}
                badgeColor={s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}
                detail={`Since ${formatDate(s.started_at)}${s.price > 0 ? ` · $${s.price}/mo` : ''}`}
                detailIcon={Calendar}
              />
            ))
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
};

// ─── Purchase History ───
// Version: 2026-06-19-v4 — defensive normalization + retry + error boundary
export const PurchaseHistoryPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [serverVersion, setServerVersion] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/user/purchase-history`, {
      headers: { ...authHeaders(), 'X-Client-Version': '2026-06-19-v4' },
      withCredentials: true,
    })
      .then(r => {
        if (cancelled) return;
        // Defensive normalization — accept any shape the backend returns
        // without crashing. Convert each row to a frontend-safe object.
        const payload = r?.data;
        const rawList = Array.isArray(payload?.purchases) ? payload.purchases : [];
        const safeList = rawList.map((p, idx) => {
          // Per-row defensive normalization. NEVER throw.
          let amountNum = 0;
          try {
            const v = Number(p?.amount);
            amountNum = Number.isFinite(v) ? v : 0;
          } catch (_) { amountNum = 0; }
          let dateStr = null;
          try {
            const d = p?.date;
            if (d instanceof Date) dateStr = d.toISOString();
            else if (typeof d === 'string') dateStr = d;
          } catch (_) { dateStr = null; }
          return {
            id: (p && (p.id || p._id)) || `row-${idx}`,
            item_type: typeof p?.item_type === 'string' ? p.item_type : '',
            product_name: typeof p?.product_name === 'string' && p.product_name ? p.product_name : null,
            creator_name: typeof p?.creator_name === 'string' && p.creator_name ? p.creator_name : null,
            amount: amountNum,
            currency: typeof p?.currency === 'string' ? p.currency : 'usd',
            status: typeof p?.status === 'string' ? p.status : 'completed',
            date: dateStr,
          };
        });
        const totalNum = Number.isFinite(payload?.total) ? payload.total : safeList.length;
        setServerVersion(r?.headers?.['x-purchase-history-version'] || r?.headers?.['X-Purchase-History-Version'] || null);
        setData({ purchases: safeList, total: totalNum });
      })
      .catch(e => {
        if (cancelled) return;
        if (e.response?.status === 401) {
          setError('Please log in to view your purchase history.');
        } else if (e.response?.status >= 500) {
          setError('We could not load your purchase history right now. Please try again in a moment or contact support@onlymass.net.');
        } else if (!e.response) {
          // Network or CORS error
          setError('We could not reach the OnlyMass server. Please check your connection and try again.');
        } else {
          // Unknown 4xx — surface an empty state with a Retry button
          setData({ purchases: [], total: 0 });
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [retryKey]);

  const typeLabel = (t) => {
    const map = { workout_plan: 'Workout', meal_plan: 'Meal Plan', subscription: 'Subscription', tip: 'Tip', stream: 'Stream' };
    return map[t] || (t ? String(t).replace(/_/g, ' ') : 'Purchase');
  };

  const typeColor = (t) => {
    const map = { workout_plan: 'bg-blue-100 text-blue-700', meal_plan: 'bg-green-100 text-green-700', subscription: 'bg-purple-100 text-purple-700', tip: 'bg-pink-100 text-pink-700' };
    return map[t] || 'bg-gray-100 text-gray-600';
  };

  // Each row is now PRE-NORMALIZED in the effect above, so render is trivial.
  // We still wrap individual row rendering in a try/catch as a final safety net.
  const renderRow = (p, i) => {
    try {
      const amountStr = `$${(p.amount || 0).toFixed(2)}`;
      const productName = p.product_name || typeLabel(p.item_type);
      return (
        <div key={p.id || `row-${i}`} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0" data-testid={`purchase-row-${i}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium text-gray-800 text-sm truncate">{productName}</p>
              <Badge className={typeColor(p.item_type)}>{typeLabel(p.item_type)}</Badge>
            </div>
            <p className="text-xs text-gray-500">
              {p.creator_name ? `${p.creator_name} · ` : ''}{formatDate(p.date)}
            </p>
          </div>
          <div className="text-right ml-3 flex-shrink-0">
            <p className="font-semibold text-gray-800">{amountStr}</p>
            <p className="text-xs text-green-600 capitalize">{p.status}</p>
          </div>
        </div>
      );
    } catch (e) {
      // Worst-case fallback for an individual row — never blank the page.
      return (
        <div key={`row-err-${i}`} className="py-3 text-xs text-gray-400 border-b border-gray-100 last:border-0" data-testid={`purchase-row-error-${i}`}>
          Could not display this purchase.
        </div>
      );
    }
  };

  if (error) {
    return (
      <PageShell testId="purchase-history-page">
        <ErrorFallback message={error} onRetry={() => setRetryKey(k => k + 1)} />
      </PageShell>
    );
  }

  return (
    <PageShell testId="purchase-history-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <ShoppingBag className="h-5 w-5 text-blue-500" /> Purchase History
            {data && <Badge variant="outline" className="ml-auto" data-testid="purchase-total-badge">{data.total}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
          ) : !data?.purchases?.length ? (
            <EmptyState icon={ShoppingBag} title="No purchases yet" description="Your product purchases, subscriptions, and tips will appear here." />
          ) : (
            data.purchases.map((p, i) => renderRow(p, i))
          )}
          {serverVersion && (
            <p className="text-[10px] text-gray-300 mt-4 text-right" data-testid="purchase-history-version">v{serverVersion}</p>
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
};

// ─── Followers / Following ───
export const FollowersFollowingPage = () => {
  const [activeTab, setActiveTab] = useState('followers');
  const [followers, setFollowers] = useState(null);
  const [following, setFollowing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/user/followers`, { headers: authHeaders(), withCredentials: true })
        .then(r => setFollowers(r.data))
        .catch(e => {
          if (e.response?.status === 401) setError('Please log in to view connections.');
          else setFollowers({ followers: [], total: 0 });
        }),
      axios.get(`${API}/user/following`, { headers: authHeaders(), withCredentials: true })
        .then(r => setFollowing(r.data))
        .catch(() => setFollowing({ following: [], total: 0 }))
    ]).finally(() => setLoading(false));
  }, []);

  if (error) return <PageShell testId="followers-following-page"><ErrorFallback message={error} /></PageShell>;

  return (
    <PageShell testId="followers-following-page">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Heart className="h-5 w-5 text-red-500" /> Connections
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full mb-4">
              <TabsTrigger value="followers" className="flex-1" data-testid="followers-tab">
                <UserCheck className="h-4 w-4 mr-1" /> Followers {followers ? `(${followers.total || 0})` : ''}
              </TabsTrigger>
              <TabsTrigger value="following" className="flex-1" data-testid="following-tab">
                <UserPlus className="h-4 w-4 mr-1" /> Following {following ? `(${following.total || 0})` : ''}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="followers">
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-red-500" /></div>
              ) : !followers?.followers?.length ? (
                <EmptyState icon={Users} title="No followers yet" description="When users follow you, they'll appear here." />
              ) : (
                (followers.followers || []).map((f, i) => (
                  <PersonRow key={i} name={f.name || 'User'} picture={f.profile_picture}
                    detail={`Followed ${formatDate(f.followed_at)}`} detailIcon={Calendar} />
                ))
              )}
            </TabsContent>

            <TabsContent value="following">
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-red-500" /></div>
              ) : !following?.following?.length ? (
                <EmptyState icon={Heart} title="Not following anyone" description="Follow creators to see their content in your feed." />
              ) : (
                (following.following || []).map((f, i) => (
                  <PersonRow key={i} name={f.name || 'User'} picture={f.profile_picture}
                    detail={`Following since ${formatDate(f.followed_at)}`} detailIcon={Calendar} />
                ))
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </PageShell>
  );
};
